import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, Lock, Target, Brain, Heart, Users, Zap, AlertCircle,
  CheckCircle, ChevronRight, RefreshCw, Award, BarChart3, Plane
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Psikoloji Testi Soruları
const PSYCHOLOGY_TESTS = {
  personality: {
    id: 'personality',
    title: 'Kişilik Testi',
    subtitle: 'MBTI benzeri kişilik analizi',
    description: 'Bu test, kişilik özelliklerinizi analiz ederek kabin memurluğu için uygunluğunuzu değerlendirir.',
    icon: 'Brain',
    color: 'from-purple-500 to-violet-600',
    duration: '15 dk',
    questionCount: 15,
    questions: [
      { id: 1, text: 'Yeni insanlarla tanışmak sizin için nasıl bir deneyim?', options: [
        { text: 'Enerji verici ve heyecan verici', score: { E: 2 } },
        { text: 'Genellikle keyifli', score: { E: 1 } },
        { text: 'Bazen yorucu olabiliyor', score: { I: 1 } },
        { text: 'Genellikle stresli', score: { I: 2 } }
      ]},
      { id: 2, text: 'Bir karar verirken neye daha çok güvenirsiniz?', options: [
        { text: 'Mantık ve analiz', score: { T: 2 } },
        { text: 'Somut veriler', score: { T: 1 } },
        { text: 'Sezgiler ve duygular', score: { F: 1 } },
        { text: 'İnsanların ihtiyaçları', score: { F: 2 } }
      ]},
      { id: 3, text: 'Planlarınız değiştiğinde nasıl hissedersiniz?', options: [
        { text: 'Rahatsız olurum', score: { J: 2 } },
        { text: 'Biraz stresli olurum', score: { J: 1 } },
        { text: 'Uyum sağlarım', score: { P: 1 } },
        { text: 'Yeni fırsatlar olarak görürüm', score: { P: 2 } }
      ]},
      { id: 4, text: 'Grup çalışmalarında hangi rol size daha uygun?', options: [
        { text: 'Lider olmak', score: { E: 2 } },
        { text: 'Koordine etmek', score: { E: 1 } },
        { text: 'Destekleyici olmak', score: { I: 1 } },
        { text: 'Bireysel katkı sunmak', score: { I: 2 } }
      ]},
      { id: 5, text: 'Stresli bir durumda önceliğiniz ne olur?', options: [
        { text: 'Sorunu çözmek', score: { T: 2 } },
        { text: 'Durumu analiz etmek', score: { T: 1 } },
        { text: 'Herkesin rahat olmasını sağlamak', score: { F: 2 } },
        { text: 'Duyguları yönetmek', score: { F: 1 } }
      ]},
      { id: 6, text: 'Uzun uçuşlarda nasıl hissedersiniz?', options: [
        { text: 'Enerjik ve aktif kalırım', score: { E: 2 } },
        { text: 'Yolcularla ilgilenmekten keyif alırım', score: { E: 1 } },
        { text: 'Kısa molaları tercih ederim', score: { I: 1 } },
        { text: 'Sakin zamanlardan faydalanırım', score: { I: 2 } }
      ]},
      { id: 7, text: 'Beklenmedik bir yolcu şikayetiyle karşılaştınız. İlk tepkiniz?', options: [
        { text: 'Durumu mantıklı bir şekilde çözerim', score: { T: 2 } },
        { text: 'Prosedürlere başvururum', score: { T: 1 } },
        { text: 'Önce yolcuyu dinlerim ve anlarım', score: { F: 2 } },
        { text: 'Empati kurarım', score: { F: 1 } }
      ]},
      { id: 8, text: 'Çalışma ortamınızı nasıl tercih edersiniz?', options: [
        { text: 'Net kurallar ve prosedürler', score: { J: 2 } },
        { text: 'Düzenli ve planlı', score: { J: 1 } },
        { text: 'Esnek ve adaptif', score: { P: 1 } },
        { text: 'Spontan ve değişken', score: { P: 2 } }
      ]},
      { id: 9, text: 'Yeni bir destinasyona gittiğinizde ilk ne yaparsınız?', options: [
        { text: 'Ekiple vakit geçiririm', score: { E: 2 } },
        { text: 'Yerel mekanları keşfederim', score: { E: 1 } },
        { text: 'Otelimde dinlenirim', score: { I: 1 } },
        { text: 'Kitap okurum veya kendimle vakit geçiririm', score: { I: 2 } }
      ]},
      { id: 10, text: 'Acil bir durumda nasıl davranırsınız?', options: [
        { text: 'Hızlı ve kararlı hareket ederim', score: { T: 2, J: 1 } },
        { text: 'Önce durumu değerlendiririm', score: { T: 1 } },
        { text: 'Panik yapan yolcuları sakinleştiririm', score: { F: 2 } },
        { text: 'Herkesin güvende olduğundan emin olurum', score: { F: 1 } }
      ]},
      { id: 11, text: 'Mesai arkadaşlarınızla ilişkileriniz nasıldır?', options: [
        { text: 'Çok sosyalim, herkesle iyi geçinirim', score: { E: 2 } },
        { text: 'Birkaç yakın arkadaşım var', score: { I: 1 } },
        { text: 'Profesyonel ilişkiler yeterli', score: { I: 2 } },
        { text: 'Genel olarak iyi', score: { E: 1 } }
      ]},
      { id: 12, text: 'Bir görevi nasıl tamamlamayı tercih edersiniz?', options: [
        { text: 'Planlı ve adım adım', score: { J: 2 } },
        { text: 'Son dakikaya kadar esnek bırakarak', score: { P: 2 } },
        { text: 'Duruma göre değişir', score: { P: 1 } },
        { text: 'Önceden hazırlanarak', score: { J: 1 } }
      ]},
      { id: 13, text: 'Takım toplantılarında nasıl davranırsınız?', options: [
        { text: 'Aktif konuşur, fikir üretirim', score: { E: 2 } },
        { text: 'Dinler, gerektiğinde katkı sunarım', score: { I: 1 } },
        { text: 'Sessiz kalır, not alırım', score: { I: 2 } },
        { text: 'Sorulara cevap veririm', score: { E: 1 } }
      ]},
      { id: 14, text: 'Bir sorunu çözerken hangi yaklaşımı tercih edersiniz?', options: [
        { text: 'Analitik düşünme ve mantık', score: { T: 2 } },
        { text: 'Sezgisel ve yaratıcı', score: { F: 1 } },
        { text: 'Başkalarının görüşlerini alma', score: { F: 2 } },
        { text: 'Deneyime dayalı', score: { T: 1 } }
      ]},
      { id: 15, text: 'Tatil planlarınız nasıldır?', options: [
        { text: 'Her şey önceden planlanmış', score: { J: 2 } },
        { text: 'Genel bir çerçeve, detaylar spontan', score: { J: 1 } },
        { text: 'Minimum plan, maksimum esneklik', score: { P: 1 } },
        { text: 'Hiç plan yapmam, anı yaşarım', score: { P: 2 } }
      ]}
    ]
  },
  emotional_intelligence: {
    id: 'emotional_intelligence',
    title: 'Duygusal Zeka (EQ)',
    subtitle: 'Empati ve duygu yönetimi',
    description: 'Bu test, duygusal zekanızı ve empati becerilerinizi ölçer.',
    icon: 'Heart',
    color: 'from-pink-500 to-rose-600',
    duration: '12 dk',
    questionCount: 12,
    questions: [
      { id: 1, text: 'Bir yolcu ağlamaya başladığında ne yaparsınız?', options: [
        { text: 'Hemen yanına gider ve ne olduğunu sorarım', score: 4 },
        { text: 'Göz teması kurar, müsait olduğumu belli ederim', score: 3 },
        { text: 'Biraz bekler, sonra yaklaşırım', score: 2 },
        { text: 'Özel alanına saygı duyar, beklerim', score: 1 }
      ]},
      { id: 2, text: 'Sinirli bir meslektaşınızla nasıl başa çıkarsınız?', options: [
        { text: 'Onu dinler ve anlamaya çalışırım', score: 4 },
        { text: 'Sakinleşmesi için zaman veririm', score: 3 },
        { text: 'Durumu görmezden gelirim', score: 1 },
        { text: 'Ona haklı olmadığını söylerim', score: 0 }
      ]},
      { id: 3, text: 'Kendi duygularınızı ne kadar iyi tanırsınız?', options: [
        { text: 'Çok iyi - duygularımın farkındayım', score: 4 },
        { text: 'İyi - çoğu zaman fark ederim', score: 3 },
        { text: 'Orta - bazen zorlanırım', score: 2 },
        { text: 'Zayıf - duygularımı anlamak zor', score: 1 }
      ]},
      { id: 4, text: 'Bir yolcu size kaba davrandığında iç tepkiniz ne olur?', options: [
        { text: 'Sakin kalır, onun gününün kötü geçtiğini düşünürüm', score: 4 },
        { text: 'Profesyonel kalır, kişisel almam', score: 3 },
        { text: 'Biraz rahatsız olurum ama göstermem', score: 2 },
        { text: 'Sinirlenebilirim', score: 1 }
      ]},
      { id: 5, text: 'Ekip içinde anlaşmazlık olduğunda rolünüz ne olur?', options: [
        { text: 'Arabulucu olurum', score: 4 },
        { text: 'Tarafsız kalmaya çalışırım', score: 3 },
        { text: 'Uzak dururum', score: 2 },
        { text: 'Taraf tutarım', score: 1 }
      ]},
      { id: 6, text: 'Yolcuların ihtiyaçlarını tahmin edebilir misiniz?', options: [
        { text: 'Evet, çoğu zaman söylemeden anlarım', score: 4 },
        { text: 'Genellikle evet', score: 3 },
        { text: 'Bazen', score: 2 },
        { text: 'Genellikle söylemeleri gerekir', score: 1 }
      ]},
      { id: 7, text: 'Stres altındayken duygularınızı nasıl yönetirsiniz?', options: [
        { text: 'Derin nefes alır, sakin kalırım', score: 4 },
        { text: 'Odaklanmaya çalışırım', score: 3 },
        { text: 'Bazen zorlanırım', score: 2 },
        { text: 'Stres duygularıma hakim olur', score: 1 }
      ]},
      { id: 8, text: 'Eleştiri aldığınızda nasıl tepki verirsiniz?', options: [
        { text: 'Yapıcı olarak değerlendirir, öğrenirim', score: 4 },
        { text: 'Dinler ve düşünürüm', score: 3 },
        { text: 'Biraz savunmacı olabilirim', score: 2 },
        { text: 'Kırılırım veya sinirlenebilirim', score: 1 }
      ]},
      { id: 9, text: 'Birinin yalan söylediğini hissedebilir misiniz?', options: [
        { text: 'Genellikle evet, beden dilinden anlarım', score: 4 },
        { text: 'Bazen fark ederim', score: 3 },
        { text: 'Nadiren', score: 2 },
        { text: 'Hayır, güvenirim', score: 1 }
      ]},
      { id: 10, text: 'Zor bir günün ardından kendinizi nasıl toparlarsınız?', options: [
        { text: 'Kendi ritüellerim var, hızlı toparlanırım', score: 4 },
        { text: 'Dinlenme ve hobilerle', score: 3 },
        { text: 'Biraz zaman alır', score: 2 },
        { text: 'Çok zorlanırım', score: 1 }
      ]},
      { id: 11, text: 'Başkalarının duygularını anlamakta ne kadar iyisiniz?', options: [
        { text: 'Çok iyi, empati yeteneğim güçlü', score: 4 },
        { text: 'İyi, genellikle anlarım', score: 3 },
        { text: 'Orta, bazen zorlanırım', score: 2 },
        { text: 'Zor buluyorum', score: 1 }
      ]},
      { id: 12, text: 'Motivasyonunuz düştüğünde ne yaparsınız?', options: [
        { text: 'Hedeflerimi hatırlar, kendimi motive ederim', score: 4 },
        { text: 'Mola veririm', score: 3 },
        { text: 'Zorla devam ederim', score: 2 },
        { text: 'Bırakmak isterim', score: 1 }
      ]}
    ]
  },
  stress_management: {
    id: 'stress_management',
    title: 'Stres Yönetimi',
    subtitle: 'Baskı altında karar verme',
    description: 'Bu test, stres altında nasıl performans gösterdiğinizi değerlendirir.',
    icon: 'Zap',
    color: 'from-amber-500 to-orange-600',
    duration: '10 dk',
    questionCount: 10,
    questions: [
      { id: 1, text: 'Uçuş geciktiğinde ve yolcular şikayet etmeye başladığında ne yaparsınız?', options: [
        { text: 'Sakin kalır, durumu açıklar ve yardımcı olurum', score: 4 },
        { text: 'Prosedürlere uygun hareket ederim', score: 3 },
        { text: 'Biraz stresli olurum ama göstermemeye çalışırım', score: 2 },
        { text: 'Panikleyebilirim', score: 1 }
      ]},
      { id: 2, text: 'Aynı anda birden fazla acil isteğiniz olduğunda ne yaparsınız?', options: [
        { text: 'Önceliklendiririm ve sırayla hallederim', score: 4 },
        { text: 'Yardım isterim', score: 3 },
        { text: 'Hepsini aynı anda yapmaya çalışırım', score: 2 },
        { text: 'Bunalırım', score: 1 }
      ]},
      { id: 3, text: 'Türbülans sırasında nasıl hissedersiniz?', options: [
        { text: 'Sakin kalırım, bu normal bir durum', score: 4 },
        { text: 'Biraz tedirgin olurum ama kontrol ederim', score: 3 },
        { text: 'Endişeli olurum', score: 2 },
        { text: 'Çok korkarım', score: 1 }
      ]},
      { id: 4, text: 'Uzun ve yorucu bir uçuştan sonra nasıl toparlanırsınız?', options: [
        { text: 'Dinlenme rutinlerim var, hızlı toparlanırım', score: 4 },
        { text: 'Uyku ve dinlenmeyle toparlanırım', score: 3 },
        { text: 'Biraz zaman alır', score: 2 },
        { text: 'Çok zorlanırım', score: 1 }
      ]},
      { id: 5, text: 'Beklenmedik bir senaryo ile karşılaştığınızda ilk tepkiniz?', options: [
        { text: 'Durumu değerlendiririm ve çözüm bulurum', score: 4 },
        { text: 'Eğitimimde öğrendiklerimi uygularım', score: 3 },
        { text: 'Yardım aramaya çalışırım', score: 2 },
        { text: 'Ne yapacağımı bilemem', score: 1 }
      ]},
      { id: 6, text: 'İş-yaşam dengenizi nasıl yönetirsiniz?', options: [
        { text: 'Net sınırlar koyar, kendime vakit ayırırım', score: 4 },
        { text: 'Genellikle denge kurarım', score: 3 },
        { text: 'Bazen zorlanırım', score: 2 },
        { text: 'Sürekli çalışırım, denge zor', score: 1 }
      ]},
      { id: 7, text: 'Uykusuzluk performansınızı nasıl etkiler?', options: [
        { text: 'Başa çıkma teknikleri biliyorum, yönetirim', score: 4 },
        { text: 'Biraz etkiler ama idare ederim', score: 3 },
        { text: 'Zorlanırım', score: 2 },
        { text: 'Çok kötü etkiler', score: 1 }
      ]},
      { id: 8, text: 'Yoğun bir dönemde nasıl organize olursunuz?', options: [
        { text: 'Önceliklendirme ve zaman yönetimi yaparım', score: 4 },
        { text: 'Liste yapar, takip ederim', score: 3 },
        { text: 'Günü kurtarmaya çalışırım', score: 2 },
        { text: 'Kaotik olurum', score: 1 }
      ]},
      { id: 9, text: 'Stresli bir durumda fiziksel belirtiler yaşar mısınız?', options: [
        { text: 'Nadiren, kontrol altında', score: 4 },
        { text: 'Bazen, ama yönetirim', score: 3 },
        { text: 'Sık sık', score: 2 },
        { text: 'Her zaman', score: 1 }
      ]},
      { id: 10, text: 'Baskı altında hata yaptığınızda ne yaparsınız?', options: [
        { text: 'Kabul eder, düzeltir ve ders çıkarırım', score: 4 },
        { text: 'Düzeltmeye çalışırım', score: 3 },
        { text: 'Kendimi suçlarım', score: 2 },
        { text: 'Panik yaparım', score: 1 }
      ]}
    ]
  },
  teamwork: {
    id: 'teamwork',
    title: 'Takım Çalışması',
    subtitle: 'İşbirliği ve iletişim becerileri',
    description: 'Bu test, takım içindeki işbirliği becerilerinizi ölçer.',
    icon: 'Users',
    color: 'from-blue-500 to-cyan-600',
    duration: '10 dk',
    questionCount: 10,
    questions: [
      { id: 1, text: 'Ekip toplantılarında genellikle hangi rolü üstlenirsiniz?', options: [
        { text: 'Aktif katılımcı ve fikir üretici', score: 4 },
        { text: 'Dinleyici ve destekçi', score: 3 },
        { text: 'Gerektiğinde katkı sunarım', score: 2 },
        { text: 'Genellikle sessiz kalırım', score: 1 }
      ]},
      { id: 2, text: 'Bir meslektaşınız hata yaptığında ne yaparsınız?', options: [
        { text: 'Özel olarak, yapıcı geri bildirim veririm', score: 4 },
        { text: 'Hatayı düzeltmesine yardım ederim', score: 3 },
        { text: 'Amirine bildiririm', score: 2 },
        { text: 'Kendi işime bakarım', score: 1 }
      ]},
      { id: 3, text: 'Ekip içinde farklı görüşler olduğunda tavrınız nedir?', options: [
        { text: 'Tüm görüşleri dinler, ortak nokta ararım', score: 4 },
        { text: 'Uzlaşı için çaba gösteririm', score: 3 },
        { text: 'Kendi görüşümü savunurum', score: 2 },
        { text: 'Tartışmadan uzak dururum', score: 1 }
      ]},
      { id: 4, text: 'Yeni bir ekip üyesine nasıl davranırsınız?', options: [
        { text: 'Hoş karşılar, adapte olmasına yardım ederim', score: 4 },
        { text: 'Sorularını yanıtlarım', score: 3 },
        { text: 'İsterse yardım ederim', score: 2 },
        { text: 'Kendi kendine öğrenir', score: 1 }
      ]},
      { id: 5, text: 'Zor bir görev paylaşımında ekip arkadaşınız adil davranmadığında?', options: [
        { text: 'Açık ve saygılı bir şekilde konuşurum', score: 4 },
        { text: 'Durumu amire iletirim', score: 3 },
        { text: 'Sessiz kalır, kendi payımı yaparım', score: 2 },
        { text: 'Kızgınlığımı belli ederim', score: 1 }
      ]},
      { id: 6, text: 'Ekip başarısını nasıl kutlarsınız?', options: [
        { text: 'Herkesi takdir eder, başarıyı paylaşırım', score: 4 },
        { text: 'Kutlama yapmayı öneririm', score: 3 },
        { text: 'İçimden mutlu olurum', score: 2 },
        { text: 'Önemli değil, görevimizdi', score: 1 }
      ]},
      { id: 7, text: 'Ekip arkadaşınız size yardım istediğinde ne yaparsınız?', options: [
        { text: 'Hemen yardım ederim', score: 4 },
        { text: 'İşim bitince yardım ederim', score: 3 },
        { text: 'Zamanım varsa yardım ederim', score: 2 },
        { text: 'Kendi işini yapmasını söylerim', score: 1 }
      ]},
      { id: 8, text: 'Ekipte bir sorun olduğunda ilk tepkiniz nedir?', options: [
        { text: 'Çözüm odaklı yaklaşırım', score: 4 },
        { text: 'Durumu anlamaya çalışırım', score: 3 },
        { text: 'Birinin çözmesini beklerim', score: 2 },
        { text: 'Uzak dururum', score: 1 }
      ]},
      { id: 9, text: 'Farklı karakterdeki insanlarla çalışmak sizin için nasıl?', options: [
        { text: 'Zenginleştirici ve öğretici', score: 4 },
        { text: 'Normal, uyum sağlarım', score: 3 },
        { text: 'Bazen zor olabiliyor', score: 2 },
        { text: 'Zorlanıyorum', score: 1 }
      ]},
      { id: 10, text: 'Ekip lideri olsanız nasıl bir lider olurdunuz?', options: [
        { text: 'Destekleyici, dinleyen ve motive eden', score: 4 },
        { text: 'Organize ve yönlendirici', score: 3 },
        { text: 'Kuralcı ve düzenli', score: 2 },
        { text: 'Otoriter', score: 1 }
      ]}
    ]
  },
  situational_judgment: {
    id: 'situational_judgment',
    title: 'Durumsal Yargı Testi (SJT)',
    subtitle: 'Senaryo bazlı karar verme',
    description: 'Bu test, gerçek kabin senaryolarında karar verme becerilerinizi ölçer.',
    icon: 'Target',
    color: 'from-emerald-500 to-teal-600',
    duration: '20 dk',
    questionCount: 12,
    questions: [
      { id: 1, text: 'Bir yolcu, yemek servisinde vejeteryan yemek istedi ama kalmamış. Ne yaparsınız?', options: [
        { text: 'Özür diler, mevcut alternatiflerden uygun olanı sunar, ekstra ikram yaparım', score: 4 },
        { text: 'Durumu açıklar ve mevcut seçenekleri sunarım', score: 3 },
        { text: 'Sadece "Maalesef bitti" derim', score: 1 },
        { text: 'Bir sonraki uçuşta rezarvasyon yapmasını öneririm', score: 2 }
      ]},
      { id: 2, text: 'Kalkıştan önce bir yolcu sarhoş olduğunu fark ettiniz. Ne yaparsınız?', options: [
        { text: 'Durumu kıdemli kabin memuruyla paylaşırım', score: 4 },
        { text: 'Yolcuyla nazikçe konuşur, su ikram ederim', score: 3 },
        { text: 'Görmezden gelirim, sorun çıkmazsa iyi', score: 1 },
        { text: 'Hemen pilot kabinine bildiririm', score: 2 }
      ]},
      { id: 3, text: 'Uçuş sırasında bir yolcu fenalaştı. Ne yaparsınız?', options: [
        { text: 'Eğitimime uygun ilk yardım yapar, anons ile doktor ararım', score: 4 },
        { text: 'Hemen kıdemli personeli çağırırım', score: 3 },
        { text: 'Yolcuya ne hissettiğini sorarım', score: 2 },
        { text: 'Panik yaparım', score: 1 }
      ]},
      { id: 4, text: 'İki yolcu arasında tartışma çıktı. Ne yaparsınız?', options: [
        { text: 'Sakin bir şekilde araya girer, ikisini de ayrı ayrı dinlerim', score: 4 },
        { text: 'Kıdemli personeli çağırırım', score: 3 },
        { text: 'Yüksek sesle sakinleşmelerini söylerim', score: 1 },
        { text: 'Biraz bekler, kendi kendine çözülür mü bakarım', score: 2 }
      ]},
      { id: 5, text: 'Bir çocuk yolcu, ailesi uyurken kayboldu ve ağlıyor. Ne yaparsınız?', options: [
        { text: 'Çocuğu sakinleştir, ailesini nazikçe uyandırır, durumu bildiririm', score: 4 },
        { text: 'Çocukla ilgilenir, ailesini ararım', score: 3 },
        { text: 'Aileyi uyandırıp çocuğu teslim ederim', score: 2 },
        { text: 'Ailenin uyanmasını beklerim', score: 1 }
      ]},
      { id: 6, text: 'Kaptan pilot, türbülans nedeniyle servis durdurulması istedi ama yolcular şikayetçi. Ne yaparsınız?', options: [
        { text: 'Güvenlik önceliğini açıklar, anlayış için teşekkür eder, durum düzelince öncelik veririm', score: 4 },
        { text: 'Kaptan pilotun kararını uygular, özür dilerim', score: 3 },
        { text: '"Kurallar böyle" derim', score: 1 },
        { text: 'Sessizce servisi durdururum', score: 2 }
      ]},
      { id: 7, text: 'Bir yolcu size telefon numaranızı sordu. Ne yaparsınız?', options: [
        { text: 'Nazikçe ve profesyonelce reddederim', score: 4 },
        { text: 'Şirket politikası gereği veremeyeceğimi söylerim', score: 3 },
        { text: 'Veririm', score: 1 },
        { text: 'Gülerek geçiştiririm', score: 2 }
      ]},
      { id: 8, text: 'Bir VIP yolcu özel muamele istiyor ve diğer yolcular fark edebilir. Ne yaparsınız?', options: [
        { text: 'Dikkat çekmeden, profesyonelce özel ilgi gösteririm', score: 4 },
        { text: 'Prosedürlere uygun şekilde ilgilenirim', score: 3 },
        { text: 'Herkese eşit davranırım', score: 2 },
        { text: 'Ne yapacağımı bilemem', score: 1 }
      ]},
      { id: 9, text: 'Yolcu kabin bagajına sığmayan bir bavul ile geldi. Ne yaparsınız?', options: [
        { text: 'Durumu nazikçe açıklar, kargo seçeneği sunarım', score: 4 },
        { text: 'Boyut kurallarını gösteririm', score: 3 },
        { text: 'Zorla yerleştirmesine izin veririm', score: 1 },
        { text: 'Başka personeli çağırırım', score: 2 }
      ]},
      { id: 10, text: 'Uçuş gecikti ve yolcular gergin. Bağlantılı uçuşu kaçıracak biri sizinle tartışıyor. Ne yaparsınız?', options: [
        { text: 'Anlayış gösterir, yer hizmetleriyle koordine eder, seçenekleri sunarım', score: 4 },
        { text: 'Durumu açıklar, yardımcı olmaya çalışırım', score: 3 },
        { text: 'Kontrolümde olmadığını söylerim', score: 2 },
        { text: 'Havayoluyla iletişime geçmesini öneririm', score: 1 }
      ]},
      { id: 11, text: 'Bir yolcu alkollü içecek istiyor ama siz zaten sarhoş olduğunu düşünüyorsunuz. Ne yaparsınız?', options: [
        { text: 'Nazikçe alternatif içecek önerir, gerekirse kıdemli personelle konuşurum', score: 4 },
        { text: 'Doğrudan reddederim', score: 3 },
        { text: 'Bir içecek daha verir, sonra keserim', score: 2 },
        { text: 'Vermem ama nedenini söylemem', score: 1 }
      ]},
      { id: 12, text: 'Bir yolcu lavaboda sigara içtiğini fark ettiniz. Ne yaparsınız?', options: [
        { text: 'Prosedüre uygun hareket eder, kaptana bildiririm', score: 4 },
        { text: 'Yolcuyu uyarırım', score: 3 },
        { text: 'Bu sefer görmezden gelirim', score: 1 },
        { text: 'Sadece kıdemli personele söylerim', score: 2 }
      ]}
    ]
  },
  communication_skills: {
    id: 'communication_skills',
    title: 'İletişim Becerileri',
    subtitle: 'Sözlü ve sözsüz iletişim',
    description: 'Kabin memurluğunda kritik olan iletişim becerilerinizi ölçer.',
    icon: 'Users',
    color: 'from-teal-500 to-green-600',
    duration: '12 dk',
    questionCount: 10,
    questions: [
      { id: 1, text: 'Yabancı bir yolcu sizi anlamadığında ne yaparsınız?', options: [
        { text: 'Basit kelimeler, el hareketleri ve görseller kullanırım', score: 4 },
        { text: 'Daha yavaş ve net konuşurum', score: 3 },
        { text: 'Başka bir personelden yardım isterim', score: 2 },
        { text: 'Tekrar aynı şeyi söylerim', score: 1 }
      ]},
      { id: 2, text: 'Bir yolcuya kötü haber vermeniz gerektiğinde nasıl yaklaşırsınız?', options: [
        { text: 'Empati kurar, durumu açıklar ve çözüm önerileri sunarım', score: 4 },
        { text: 'Profesyonel bir şekilde durumu aktarırım', score: 3 },
        { text: 'Doğrudan söylerim', score: 2 },
        { text: 'Mümkünse başkasının söylemesini tercih ederim', score: 1 }
      ]},
      { id: 3, text: 'Anons yaparken dikkat ettiğiniz en önemli şey nedir?', options: [
        { text: 'Net, anlaşılır ve sakin bir ses tonu', score: 4 },
        { text: 'Tüm bilgilerin eksiksiz verilmesi', score: 3 },
        { text: 'Hızlı olmak', score: 2 },
        { text: 'Metni okumak', score: 1 }
      ]},
      { id: 4, text: 'Bir yolcu size uzun uzun şikayet ediyor. Ne yaparsınız?', options: [
        { text: 'Aktif dinler, göz teması kurar, özetleyerek anladığımı gösteririm', score: 4 },
        { text: 'Sabırla dinler, not alırım', score: 3 },
        { text: 'Dinler ama bazen sözünü keserim', score: 2 },
        { text: 'Bir an önce çözüm sunmaya çalışırım', score: 1 }
      ]},
      { id: 5, text: 'Beden diliniz hakkında ne düşünüyorsunuz?', options: [
        { text: 'Farkındayım ve bilinçli kullanırım', score: 4 },
        { text: 'Genellikle dikkat ederim', score: 3 },
        { text: 'Çok düşünmem', score: 2 },
        { text: 'Önemli olduğunu düşünmüyorum', score: 1 }
      ]},
      { id: 6, text: 'Çok kültürlü bir ortamda iletişim kurarken nelere dikkat edersiniz?', options: [
        { text: 'Kültürel farklılıklara saygı, uygun beden dili ve sabır', score: 4 },
        { text: 'Nazik ve saygılı olmak', score: 3 },
        { text: 'Standart prosedürlere uymak', score: 2 },
        { text: 'Herkese aynı şekilde davranmak', score: 1 }
      ]},
      { id: 7, text: 'Yazılı iletişimde (raporlama) güçlü müsünüz?', options: [
        { text: 'Evet, net ve düzenli raporlar yazarım', score: 4 },
        { text: 'İyi, gerekli bilgileri aktarırım', score: 3 },
        { text: 'Orta, bazen zorlanırım', score: 2 },
        { text: 'Yazılı iletişim benim için zor', score: 1 }
      ]},
      { id: 8, text: 'Geri bildirim verirken nasıl bir yaklaşım benimsersiniz?', options: [
        { text: 'Sandviç tekniği: olumlu-gelişim alanı-olumlu', score: 4 },
        { text: 'Yapıcı ve çözüm odaklı', score: 3 },
        { text: 'Doğrudan ve dürüst', score: 2 },
        { text: 'Geri bildirim vermekten kaçınırım', score: 1 }
      ]},
      { id: 9, text: 'Zor bir konuyu açıklamanız gerektiğinde ne yaparsınız?', options: [
        { text: 'Basitleştirir, örnekler veririm', score: 4 },
        { text: 'Adım adım anlatırım', score: 3 },
        { text: 'Olduğu gibi anlatırım', score: 2 },
        { text: 'Kaçınmaya çalışırım', score: 1 }
      ]},
      { id: 10, text: 'Bir yolcu yanlış anlaşıldığını düşünüyor. Ne yaparsınız?', options: [
        { text: 'Tekrar dinler, anladığımı kendi kelimelerimle ifade ederim', score: 4 },
        { text: 'Özür diler, açıklama yaparım', score: 3 },
        { text: 'Kendimi savunurum', score: 2 },
        { text: 'Tartışmam', score: 1 }
      ]}
    ]
  },
  cultural_awareness: {
    id: 'cultural_awareness',
    title: 'Kültürel Farkındalık',
    subtitle: 'Çok kültürlü ortam yetkinliği',
    description: 'Farklı kültürlerle çalışma ve iletişim becerilerinizi değerlendirir.',
    icon: 'Plane',
    color: 'from-blue-600 to-indigo-700',
    duration: '10 dk',
    questionCount: 8,
    questions: [
      { id: 1, text: 'Farklı bir kültürden bir yolcu, sizce garip bir istekte bulundu. Ne yaparsınız?', options: [
        { text: 'Saygıyla dinler, mümkünse karşılamaya çalışırım', score: 4 },
        { text: 'Yapabiliyorsam yaparım', score: 3 },
        { text: 'Standart prosedürü uygularım', score: 2 },
        { text: 'Yapamayacağımı söylerim', score: 1 }
      ]},
      { id: 2, text: 'Bir yolcunın dini inancı nedeniyle özel yemek gereksinimi var. Ne yaparsınız?', options: [
        { text: 'Önceden hazırlanmış özel yemeği sunar, başka ihtiyacı var mı sorarım', score: 4 },
        { text: 'Elimden geleni yaparım', score: 3 },
        { text: 'Varsa veririm', score: 2 },
        { text: '"Önceden söylemeliydiniz" derim', score: 1 }
      ]},
      { id: 3, text: 'Farklı ülkelerden gelen ekip arkadaşlarınızla çalışırken zorlandığınız durumlar oldu mu?', options: [
        { text: 'Farklılıkları zenginlik olarak görür, öğrenmeye açık olurum', score: 4 },
        { text: 'Bazen zor ama uyum sağlarım', score: 3 },
        { text: 'Ara sıra iletişim sorunları yaşarım', score: 2 },
        { text: 'Evet, zor buluyorum', score: 1 }
      ]},
      { id: 4, text: 'Bir yolcunun el sıkışmak istememesi sizi rahatsız eder mi?', options: [
        { text: 'Hayır, kültürel farklılıklara saygı duyarım', score: 4 },
        { text: 'Anlarım, sorun olmaz', score: 3 },
        { text: 'Biraz garip hissederim', score: 2 },
        { text: 'Evet, kırılabilirim', score: 1 }
      ]},
      { id: 5, text: 'Hiç gitmediğiniz bir ülkeye uçuşunuz var. Ne yaparsınız?', options: [
        { text: 'Önceden kültür, gelenekler ve temel kelimeler hakkında araştırma yaparım', score: 4 },
        { text: 'Genel bilgi edinirim', score: 3 },
        { text: 'Orada öğrenirim', score: 2 },
        { text: 'Farketmez, her yerde aynı iş', score: 1 }
      ]},
      { id: 6, text: 'Bir yolcu kendi dilinde konuşuyor ve siz anlamıyorsunuz. Ne hissedersiniz?', options: [
        { text: 'Alternatif iletişim yolları ararım, sabırlı olurum', score: 4 },
        { text: 'Yardım etmeye çalışırım', score: 3 },
        { text: 'Biraz zorlanırım', score: 2 },
        { text: 'İngilizce konuşmasını beklerim', score: 1 }
      ]},
      { id: 7, text: 'Kendi kültürünüzden farklı değerlere sahip biriyle çalışmak zorunda kalsanız?', options: [
        { text: 'Saygı duyar, profesyonel çalışırım', score: 4 },
        { text: 'Uyum sağlamaya çalışırım', score: 3 },
        { text: 'Zorluk çekebilirim', score: 2 },
        { text: 'Çok zor olur', score: 1 }
      ]},
      { id: 8, text: 'Kültürel bir yanlış anlaşılma olduğunda ne yaparsınız?', options: [
        { text: 'İçtenlikle özür diler, öğrenmeye açık olduğumu gösteririm', score: 4 },
        { text: 'Özür diler, açıklarım', score: 3 },
        { text: 'Durumu geçiştiririm', score: 2 },
        { text: 'Savunmaya geçerim', score: 1 }
      ]}
    ]
  }
};
const PERSONALITY_TYPES = {
  'ESFJ': {
    title: 'Ev Sahibi',
    description: 'Sıcak, işbirlikçi ve uyumlu. Yolcuların ihtiyaçlarına karşı çok duyarlı.',
    strengths: ['Mükemmel müşteri hizmeti', 'Takım oyuncusu', 'Güvenilir'],
    improvement: ['Eleştiriye karşı hassasiyet']
  },
  'ENFJ': {
    title: 'Karizmatik Lider',
    description: 'İlham verici, empatik ve organize. Doğal liderlik özellikleri.',
    strengths: ['Liderlik', 'İletişim', 'Motivasyon'],
    improvement: ['Aşırı idealist olabilir']
  },
  'ISFJ': {
    title: 'Koruyucu',
    description: 'Özenli, sadık ve detay odaklı. Güvenlik prosedürlerinde mükemmel.',
    strengths: ['Detaylara dikkat', 'Güvenilirlik', 'Sabır'],
    improvement: ['Değişime adaptasyon']
  },
  'ESFP': {
    title: 'Entertainer',
    description: 'Enerjik, eğlenceli ve esnek. Yolcuları mutlu etmede harika.',
    strengths: ['Enerji', 'Esneklik', 'Pozitiflik'],
    improvement: ['Uzun vadeli planlama']
  },
  'default': {
    title: 'Potansiyel Kabin Memuru',
    description: 'Çeşitli güçlü yönlere sahip, gelişime açık bir profil.',
    strengths: ['Adaptasyon yeteneği', 'Öğrenme isteği'],
    improvement: ['Spesifik alanlarda gelişim']
  }
};

export default function PsychologyTests() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [activeTest, setActiveTest] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completedTests, setCompletedTests] = useState([]);
  
  const isUnlocked = user?.role === 'admin' || user?.psychology_tests_unlocked === true || user?.psychology_tests_unlocked === 'true';
  
  // Debug log
  useEffect(() => {
    console.log('PsychologyTests - user:', user);
    console.log('PsychologyTests - isUnlocked:', isUnlocked);
    console.log('PsychologyTests - role:', user?.role);
    console.log('PsychologyTests - psychology_tests_unlocked:', user?.psychology_tests_unlocked);
  }, [user, isUnlocked]);

  useEffect(() => {
    // Tamamlanan testleri yükle
    const loadCompletedTests = async () => {
      try {
        const res = await axios.get(`${API}/psychology/completed`, { withCredentials: true });
        setCompletedTests(res.data.completed || []);
      } catch (error) {
        console.log('Could not load completed tests');
      }
    };
    if (isUnlocked) loadCompletedTests();
  }, [isUnlocked]);

  const startTest = (testId) => {
    setActiveTest(testId);
    setCurrentQuestion(0);
    setAnswers({});
    setTestResult(null);
  };

  const handleAnswer = (questionId, optionIndex, score) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { optionIndex, score }
    }));
  };

  const nextQuestion = () => {
    const test = PSYCHOLOGY_TESTS[activeTest];
    if (currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const calculateResults = () => {
    setLoading(true);
    const test = PSYCHOLOGY_TESTS[activeTest];
    
    setTimeout(() => {
      let result = {};
      
      if (activeTest === 'personality') {
        // MBTI tarzı hesaplama
        const scores = { E: 0, I: 0, T: 0, F: 0, J: 0, P: 0 };
        Object.values(answers).forEach(answer => {
          if (answer.score) {
            Object.entries(answer.score).forEach(([key, value]) => {
              scores[key] = (scores[key] || 0) + value;
            });
          }
        });
        
        const type = 
          (scores.E >= scores.I ? 'E' : 'I') +
          'S' + // Simplified
          (scores.T >= scores.F ? 'T' : 'F') +
          (scores.J >= scores.P ? 'J' : 'P');
        
        const personalityInfo = PERSONALITY_TYPES[type] || PERSONALITY_TYPES['default'];
        
        result = {
          type,
          ...personalityInfo,
          scores,
          percentage: Math.round((Object.values(answers).length / test.questions.length) * 100)
        };
      } else {
        // Puan bazlı hesaplama
        let totalScore = 0;
        let maxScore = test.questions.length * 4;
        
        Object.values(answers).forEach(answer => {
          if (typeof answer.score === 'number') {
            totalScore += answer.score;
          }
        });
        
        const percentage = Math.round((totalScore / maxScore) * 100);
        
        let level, description;
        if (percentage >= 80) {
          level = 'Mükemmel';
          description = 'Bu alanda çok güçlüsünüz. Kabin memurluğu için ideal özellikler gösteriyorsunuz.';
        } else if (percentage >= 60) {
          level = 'İyi';
          description = 'Bu alanda iyi bir seviyedesiniz. Biraz geliştirmeyle mükemmel olabilirsiniz.';
        } else if (percentage >= 40) {
          level = 'Orta';
          description = 'Gelişim potansiyeliniz var. Pratik yaparak bu alanı güçlendirebilirsiniz.';
        } else {
          level = 'Geliştirilmeli';
          description = 'Bu alan üzerinde çalışmanız önerilir. Eğitimlerle kendinizi geliştirebilirsiniz.';
        }
        
        result = {
          score: totalScore,
          maxScore,
          percentage,
          level,
          description
        };
      }
      
      setTestResult(result);
      setLoading(false);
      
      // Sonucu kaydet
      saveResult(activeTest, result);
    }, 1500);
  };

  const saveResult = async (testId, result) => {
    try {
      await axios.post(`${API}/psychology/save-result`, {
        test_id: testId,
        result
      }, { withCredentials: true });
      setCompletedTests(prev => [...prev, testId]);
    } catch (error) {
      console.log('Could not save result');
    }
  };

  const resetTest = () => {
    setActiveTest(null);
    setCurrentQuestion(0);
    setAnswers({});
    setTestResult(null);
  };

  // Test içi görünüm
  if (activeTest && !testResult) {
    const test = PSYCHOLOGY_TESTS[activeTest];
    const question = test.questions[currentQuestion];
    const Icon = test.icon;
    const progress = ((currentQuestion + 1) / test.questions.length) * 100;
    const isAnswered = answers[question.id] !== undefined;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Header */}
        <div className={`bg-gradient-to-r ${test.color} px-4 py-4`}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <button onClick={resetTest} className="text-white/80 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <h1 className="text-lg font-bold text-white">{test.title}</h1>
                <p className="text-white/70 text-sm">{currentQuestion + 1} / {test.questions.length}</p>
              </div>
              <div className="w-5"></div>
            </div>
            <Progress value={progress} className="mt-3 h-2" />
          </div>
        </div>

        {/* Question */}
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-white mb-6">{question.text}</h2>
              
              <div className="space-y-3">
                {question.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(question.id, idx, option.score)}
                    className={`w-full p-4 rounded-xl text-left transition-all ${
                      answers[question.id]?.optionIndex === idx
                        ? 'bg-white/20 border-2 border-white/50'
                        : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                    }`}
                  >
                    <span className="text-white">{option.text}</span>
                  </button>
                ))}
              </div>

              <Button
                onClick={nextQuestion}
                disabled={!isAnswered}
                className={`w-full mt-6 ${isAnswered ? 'bg-gradient-to-r ' + test.color : 'bg-gray-600'}`}
              >
                {currentQuestion < test.questions.length - 1 ? 'Sonraki Soru' : 'Testi Bitir'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Sonuç görünümü
  if (testResult) {
    const test = PSYCHOLOGY_TESTS[activeTest];
    const Icon = test.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className={`bg-gradient-to-r ${test.color} px-4 py-6`}>
          <div className="max-w-4xl mx-auto text-center">
            <Icon className="w-16 h-16 text-white mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white">{test.title} Sonuçları</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardContent className="p-6">
              {activeTest === 'personality' ? (
                <>
                  <div className="text-center mb-6">
                    <div className="text-6xl font-bold text-white mb-2">{testResult.type}</div>
                    <h2 className="text-2xl font-bold text-purple-300">{testResult.title}</h2>
                    <p className="text-white/70 mt-2">{testResult.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-500/20 rounded-xl p-4">
                      <h3 className="text-green-400 font-bold mb-2">Güçlü Yönler</h3>
                      <ul className="text-white/80 text-sm space-y-1">
                        {testResult.strengths.map((s, i) => (
                          <li key={i}>✓ {s}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-amber-500/20 rounded-xl p-4">
                      <h3 className="text-amber-400 font-bold mb-2">Gelişim Alanları</h3>
                      <ul className="text-white/80 text-sm space-y-1">
                        {testResult.improvement.map((s, i) => (
                          <li key={i}>→ {s}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <svg className="w-32 h-32 transform -rotate-90">
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" className="text-white/10" />
                        <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" 
                          className="text-emerald-400"
                          strokeDasharray={`${testResult.percentage * 3.52} 352`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-3xl font-bold text-white">{testResult.percentage}%</span>
                      </div>
                    </div>
                    <h2 className="text-2xl font-bold text-emerald-400">{testResult.level}</h2>
                    <p className="text-white/70 mt-2">{testResult.description}</p>
                  </div>
                </>
              )}

              <div className="flex gap-3 mt-6">
                <Button onClick={resetTest} variant="outline" className="flex-1 border-white/30 text-white">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Yeni Test
                </Button>
                <Button onClick={() => navigate('/dashboard')} className={`flex-1 bg-gradient-to-r ${test.color}`}>
                  Ana Menü
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Erişim kilitli
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">Psikolojik Testler</h1>
                <p className="text-indigo-100 text-sm">Kişilik ve yetenek analizi</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-md mx-auto px-4 py-12">
          <Card className="border-0 bg-white/10 backdrop-blur-lg text-center">
            <CardContent className="p-8">
              <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-purple-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-3">Erişim Kilitli</h2>
              <p className="text-white/60 mb-6">
                Psikolojik Testler özelliği henüz sizin için aktif edilmemiş.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-purple-500 hover:bg-purple-600 text-white"
              >
                Ana Menüye Dön
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Ana liste görünümü
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Psikolojik Testler</h1>
              <p className="text-indigo-100 text-sm">Kabin memurluğu için kişilik analizi</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Info Card */}
        <Card className="mb-6 border-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-lg">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Plane className="w-6 h-6 text-amber-400 flex-shrink-0" />
              <div>
                <h4 className="text-white font-medium mb-1">Neden Bu Testler?</h4>
                <p className="text-white/70 text-sm">
                  Havayolu şirketleri mülakatlarda psikolojik testler uygular. 
                  Bu testlerle kendinizi tanıyın ve mülakata hazırlanın.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test List */}
        <div className="space-y-4">
          {Object.values(PSYCHOLOGY_TESTS).map((test) => {
            const Icon = test.icon;
            const isCompleted = completedTests.includes(test.id);
            
            return (
              <Card 
                key={test.id}
                className="border-0 bg-white/10 backdrop-blur-lg hover:bg-white/15 transition-all cursor-pointer"
                onClick={() => startTest(test.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 bg-gradient-to-br ${test.color} rounded-xl flex items-center justify-center relative`}>
                      <Icon className="w-8 h-8 text-white" />
                      {isCompleted && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white text-lg">{test.title}</h3>
                      <p className="text-white/60 text-sm">{test.subtitle}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-white/40 bg-white/10 px-2 py-1 rounded">
                          {test.questions.length} soru
                        </span>
                        <span className="text-xs text-white/40 bg-white/10 px-2 py-1 rounded">
                          {test.duration}
                        </span>
                        {isCompleted && (
                          <span className="text-xs text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded">
                            ✓ Tamamlandı
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/30" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
